import React, { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Briefcase, FileText, MessageSquare, Clock, CheckCircle2, AlertCircle, Download, Upload, Inbox } from 'lucide-react';
import {
  fetchPortalMatter, listPortalInvoices, listPortalDocumentsForMatter,
  listPortalDocumentRequestsForClient, portalDocumentSignedUrl,
  respondPortalDocumentRequest, portalUploadDocument, uploadPortalFile,
} from '../../lib/db';
import { formatDate, formatMoney } from '../../lib/formatters';
import { useRoute } from '../../lib/router';
import { usePortal } from '../../contexts/PortalContext';
import toast from '../../lib/toast';

const Field = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-slate-900 dark:text-white">{value}</p>
    </div>
  );
};

const STATUS_COLOR = {
  open:        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  closed:      'bg-slate-100 text-slate-600',
  archived:    'bg-slate-100 text-slate-500',
};

const INV_STATUS = {
  draft:   { tone: 'bg-slate-100 text-slate-600', label: 'Draft' },
  sent:    { tone: 'bg-blue-100 text-blue-700', label: 'Sent' },
  paid:    { tone: 'bg-emerald-100 text-emerald-700', label: 'Paid' },
  overdue: { tone: 'bg-red-100 text-red-700', label: 'Overdue' },
  void:    { tone: 'bg-slate-100 text-slate-400', label: 'Void' },
};

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all duration-150 whitespace-nowrap',
        active
          ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300 bg-indigo-600/5 dark:bg-indigo-500/10 rounded-t-lg'
          : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Timeline({ matter }) {
  const events = [];

  if (matter.created_at || matter.open_date) {
    events.push({
      date: matter.open_date || matter.created_at,
      icon: Briefcase,
      color: 'bg-indigo-500',
      title: 'Case file opened',
      sub: matter.practice_area || '',
    });
  }

  if (matter.status === 'in_progress' || matter.status === 'open') {
    events.push({
      date: matter.updated_at || matter.created_at,
      icon: Clock,
      color: 'bg-amber-500',
      title: 'Case in progress',
      sub: 'Your attorney is actively working on this matter',
    });
  }

  if (matter.statute_of_limitations) {
    const sol = new Date(matter.statute_of_limitations);
    const daysLeft = Math.ceil((sol - Date.now()) / 86400000);
    events.push({
      date: matter.statute_of_limitations,
      icon: AlertCircle,
      color: daysLeft < 30 ? 'bg-red-500' : daysLeft < 90 ? 'bg-amber-500' : 'bg-slate-400',
      title: 'Statute of limitations',
      sub: daysLeft > 0 ? `${daysLeft} days remaining` : 'Expired',
    });
  }

  if (matter.closed_at) {
    events.push({
      date: matter.closed_at,
      icon: CheckCircle2,
      color: 'bg-emerald-500',
      title: 'Case file closed',
      sub: matter.closing_reason || '',
    });
  }

  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  if (!events.length) return (
    <div className="text-center py-10 text-slate-400 text-sm">No timeline events yet.</div>
  );

  return (
    <div className="relative pl-8 space-y-6 py-2">
      <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200 dark:bg-slate-700" />
      {events.map((ev, i) => {
        const Icon = ev.icon;
        return (
          <div key={i} className="relative flex gap-4">
            <div className={`absolute -left-5 w-5 h-5 rounded-full flex items-center justify-center ${ev.color} shadow-sm`}>
              <Icon size={11} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white">{ev.title}</p>
              {ev.sub && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{ev.sub}</p>}
              <p className="text-xs text-slate-400 mt-1">{formatDate(ev.date)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PortalMatterDetailPage({ matterId }) {
  const [matter,   setMatter]   = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [docs,     setDocs]     = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab,      setTab]      = useState('details');
  const [loading,  setLoading]  = useState(true);
  const [uploading, setUploading] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null); // request the user is responding to
  const [, navigate] = useRoute();
  const { contact, firm } = usePortal();
  const adHocInputRef = React.useRef(null);
  const respondInputRef = React.useRef(null);

  const refreshRequests = useCallback(() => {
    listPortalDocumentRequestsForClient().then(({ data }) => {
      const all = Array.isArray(data) ? data : [];
      setRequests(all.filter((r) => !r.matter_id || r.matter_id === matterId));
    });
  }, [matterId]);

  useEffect(() => {
    if (!matterId) { setLoading(false); return; }
    Promise.all([
      fetchPortalMatter(matterId),
      listPortalInvoices(),
    ]).then(([{ data: m }, { data: inv }]) => {
      setMatter(m);
      setInvoices((inv || []).filter((i) => i.matter_id === matterId));
      setLoading(false);
    });
    listPortalDocumentsForMatter(matterId).then(({ data }) => setDocs(data || []));
    refreshRequests();
  }, [matterId, refreshRequests]);

  const handleDownload = async (doc) => {
    const { signedUrl, error } = await portalDocumentSignedUrl(doc.id);
    if (error || !signedUrl) {
      toast.error(error?.message || 'Could not generate download link.');
      return;
    }
    window.open(signedUrl, '_blank', 'noopener,noreferrer');
  };

  const doAdHocUpload = async (file) => {
    if (!file || !firm?.id || !contact?.id) return;
    setUploading(true);
    const up = await uploadPortalFile(firm.id, contact.id, matterId, file);
    if (up.error) { setUploading(false); toast.error('Upload failed: ' + up.error.message); return; }
    const { error } = await portalUploadDocument(matterId, up.path, file.name, up.filename, up.mime, up.size);
    setUploading(false);
    if (error) { toast.error('Could not register upload: ' + error.message); return; }
    toast.success('Document sent to your attorney.');
    listPortalDocumentsForMatter(matterId).then(({ data }) => setDocs(data || []));
  };

  const doRespondUpload = async (file) => {
    if (!file || !respondingTo || !firm?.id || !contact?.id) return;
    setUploading(true);
    const up = await uploadPortalFile(firm.id, contact.id, matterId, file);
    if (up.error) { setUploading(false); toast.error('Upload failed: ' + up.error.message); return; }
    const { error } = await respondPortalDocumentRequest(respondingTo.id, up.path, up.filename, up.mime, up.size);
    setUploading(false);
    if (error) { toast.error('Could not submit: ' + error.message); return; }
    toast.success('Uploaded for review.');
    setRespondingTo(null);
    refreshRequests();
  };

  if (loading) return <div className="text-center py-20 text-slate-400">Loading…</div>;
  if (!matter) return (
    <div className="text-center py-20 text-slate-400">
      <Briefcase size={40} className="mx-auto mb-4 opacity-30" />
      <p>Matter not found.</p>
    </div>
  );

  const totalOwed = invoices.filter((i) => i.status !== 'paid' && i.status !== 'void').reduce((s, i) => s + Number(i.balance || 0), 0);

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate('/portal/matters')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 transition"
      >
        <ArrowLeft size={14} /> Back to case files
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${STATUS_COLOR[matter.status] || STATUS_COLOR.open}`}>
                {matter.status?.replace('_', ' ')}
              </span>
              <span className="text-xs text-slate-400">{matter.matter_number}</span>
            </div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{matter.title}</h1>
            {matter.description && (
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{matter.description}</p>
            )}
          </div>
          {totalOwed > 0 && (
            <div className="text-right flex-shrink-0 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-600 font-medium">Outstanding balance</p>
              <p className="text-lg font-bold font-mono text-amber-700 dark:text-amber-400">{formatMoney(totalOwed)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
        <TabBtn active={tab === 'details'}  onClick={() => setTab('details')}>Details</TabBtn>
        <TabBtn active={tab === 'timeline'} onClick={() => setTab('timeline')}>
          Timeline
        </TabBtn>
        <TabBtn active={tab === 'invoices'} onClick={() => setTab('invoices')}>
          Invoices {invoices.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-200 dark:bg-slate-700 rounded-full">{invoices.length}</span>}
        </TabBtn>
        <TabBtn active={tab === 'documents'} onClick={() => setTab('documents')}>
          Documents {docs.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-200 dark:bg-slate-700 rounded-full">{docs.length}</span>}
        </TabBtn>
        <TabBtn active={tab === 'requests'} onClick={() => setTab('requests')}>
          Requests {requests.filter((r) => r.status === 'open' || r.status === 'rejected').length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-200 dark:bg-amber-700 text-amber-800 dark:text-amber-100 rounded-full">
              {requests.filter((r) => r.status === 'open' || r.status === 'rejected').length}
            </span>
          )}
        </TabBtn>
      </div>

      {/* Details tab */}
      {tab === 'details' && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Case file details</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            <Field label="Practice area"  value={matter.practice_area} />
            <Field label="Jurisdiction"   value={matter.jurisdiction} />
            <Field label="Court"          value={matter.court} />
            <Field label="Case number"    value={matter.court_case_number} />
            <Field label="Fee type"       value={matter.fee_arrangement?.replace('_', ' ')} />
            <Field label="Date opened"    value={formatDate(matter.open_date || matter.created_at)} />
            {matter.statute_of_limitations && (
              <Field label="Statute of limitations" value={formatDate(matter.statute_of_limitations)} />
            )}
          </div>
        </div>
      )}

      {/* Timeline tab */}
      {tab === 'timeline' && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-6">Matter timeline</h2>
          <Timeline matter={matter} />
        </div>
      )}

      {/* Invoices tab */}
      {tab === 'invoices' && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
          {!invoices.length ? (
            <div className="text-center py-12 text-slate-400 text-sm">No invoices for this matter yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  <th className="text-left px-6 py-3 font-medium">Invoice</th>
                  <th className="text-right px-4 py-3 font-medium">Amount</th>
                  <th className="text-right px-4 py-3 font-medium">Paid</th>
                  <th className="text-right px-4 py-3 font-medium">Balance</th>
                  <th className="text-left px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {invoices.map((inv) => {
                  const s = INV_STATUS[inv.status] || INV_STATUS.sent;
                  return (
                    <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-6 py-3">
                        <p className="font-medium">{inv.invoice_number || inv.id.slice(0,8)}</p>
                        <p className="text-xs text-slate-400">{formatDate(inv.issue_date)}</p>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatMoney(inv.total)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatMoney(inv.amount_paid)}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">{formatMoney(inv.balance)}</td>
                      <td className="px-6 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.tone}`}>{s.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 font-semibold">
                  <td className="px-6 py-3 text-sm">Total</td>
                  <td className="px-4 py-3 text-right font-mono">{formatMoney(invoices.reduce((s,i)=>s+Number(i.total||0),0))}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatMoney(invoices.reduce((s,i)=>s+Number(i.amount_paid||0),0))}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatMoney(invoices.reduce((s,i)=>s+Number(i.balance||0),0))}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Documents tab */}
      {tab === 'documents' && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
            <p className="text-sm text-slate-500">Documents shared with you, and uploads you've sent.</p>
            <input
              ref={adHocInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) doAdHocUpload(f);
                if (adHocInputRef.current) adHocInputRef.current.value = '';
              }}
            />
            <button
              onClick={() => adHocInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 transition"
            >
              <Upload size={14} />
              {uploading ? 'Uploading…' : 'Upload document'}
            </button>
          </div>
          {!docs.length ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              <FileText size={32} className="mx-auto mb-3 opacity-30" />
              No documents yet. Use "Upload document" to send one to your attorney.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {docs.map((doc) => (
                <li key={doc.id} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                      <FileText size={16} className="text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{doc.title}</p>
                      <p className="text-xs text-slate-400">{doc.category || 'Document'} · {formatDate(doc.created_at)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="flex-shrink-0 p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition"
                    title="Download"
                  >
                    <Download size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Requests tab */}
      {tab === 'requests' && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
          <input
            ref={respondInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) doRespondUpload(f);
              if (respondInputRef.current) respondInputRef.current.value = '';
            }}
          />
          {!requests.length ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              <Inbox size={32} className="mx-auto mb-3 opacity-30" />
              No document requests for this matter.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {requests.map((r) => {
                const statusTone = {
                  open:      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                  uploaded:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                  accepted:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
                  rejected:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                  cancelled: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                }[r.status] || 'bg-slate-100 text-slate-600';
                const statusLabel = {
                  open:      'Action needed',
                  uploaded:  'Awaiting review',
                  accepted:  'Accepted',
                  rejected:  'Rejected — please re-upload',
                  cancelled: 'Cancelled',
                }[r.status] || r.status;
                const canUpload = r.status === 'open' || r.status === 'rejected';
                return (
                  <li key={r.id} className="px-6 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${statusTone}`}>
                            {statusLabel}
                          </span>
                          <span className="text-xs text-slate-400">
                            Requested {formatDate(r.created_at)}
                            {r.due_at ? ` · due ${formatDate(r.due_at)}` : ''}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{r.title}</p>
                        {r.description && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{r.description}</p>
                        )}
                        {r.upload_filename && (
                          <p className="text-xs text-slate-400 mt-1">
                            You sent: {r.upload_filename}
                            {r.uploaded_at ? ` (${formatDate(r.uploaded_at)})` : ''}
                          </p>
                        )}
                        {r.status === 'rejected' && r.review_note && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Attorney note: {r.review_note}
                          </p>
                        )}
                      </div>
                      {canUpload && (
                        <button
                          onClick={() => { setRespondingTo(r); respondInputRef.current?.click(); }}
                          disabled={uploading}
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 transition"
                        >
                          <Upload size={14} />
                          {uploading && respondingTo?.id === r.id ? 'Uploading…' : 'Upload'}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Message prompt */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200">Have a question about this matter?</p>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5">Send your attorney a message and they'll get back to you shortly.</p>
        </div>
        <button
          onClick={() => navigate('/portal/messages')}
          className="flex-shrink-0 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition"
        >
          <MessageSquare size={14} className="inline mr-1.5" />Message
        </button>
      </div>
    </div>
  );
}
